import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import Section from '../components/Section';

const values = [
  {
    title: 'Custom over generic',
    body: 'Templates are convenient until they limit you. We build software shaped to your real workflow — not the other way around.',
  },
  {
    title: 'Small, senior team',
    body: 'No layered hand-offs. The people designing your product are the same people writing the code.',
  },
  {
    title: 'Boring, durable choices',
    body: 'We pick the technology that will still be a good decision in three years. Excitement is not a strategy.',
  },
  {
    title: 'Honest scope',
    body: 'We will tell you when something is a bad idea, when something is overbuilt, and when an off-the-shelf tool is enough.',
  },
];

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Nexsol Labs',
  url: 'https://nexsollabs.com/about',
  mainEntity: {
    '@type': 'Person',
    name: 'Sasho Cvetanoski',
    jobTitle: 'Founder & Lead Engineer',
    description:
      'Electrical engineer turned software builder. Founder of Nexsol Labs, building custom software solutions for companies across Macedonia, the Balkans, and Europe.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Skopje',
      addressCountry: 'MK',
    },
    worksFor: { '@id': 'https://nexsollabs.com/#organization' },
  },
};

const aboutBreadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nexsollabs.com/' },
    { '@type': 'ListItem', position: 2, name: 'About', item: 'https://nexsollabs.com/about' },
  ],
};

export default function About() {
  return (
    <>
      <SEO
        title="About Nexsol Labs — Custom Software Studio in Skopje"
        description="Meet the founder of Nexsol Labs — a Skopje-based custom software studio building web platforms, SaaS, and AI-powered tools for clients across North Macedonia, the Balkans, and Europe."
        path="/about"
        keywords="Nexsol Labs founder, Sasho Cvetanoski, software studio Skopje, custom software Macedonia, софтверска студио Скопје, software engineer Skopje, electrical engineer software builder"
        jsonLd={[aboutJsonLd, aboutBreadcrumbJsonLd]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-page relative py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="eyebrow">About Nexsol Labs</p>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-white md:text-5xl">
              We build the software your business actually needs —{' '}
              <span className="gradient-text">nothing more, nothing less.</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-white/70 md:text-lg">
              Nexsol Labs is a custom software engineering studio. We partner with
              founders, operations leaders, and technical teams who need real software
              built — not another off-the-shelf tool wedged into the wrong workflow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOUNDER */}
      <Section
        eyebrow="Founder"
        title="Hi, I'm the person you'll actually talk to."
      >
        <div className="grid items-center gap-10 md:grid-cols-[280px_1fr]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative mx-auto w-full max-w-[280px]"
          >
            <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-accent-500/40 to-violet-500/40 blur-2xl" />
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/5">
              <img
                src="/founder.jpg"
                alt="Sasho Cvetanoski — founder of Nexsol Labs"
                className="aspect-[4/5] w-full object-cover"
                loading="lazy"
                width={560}
                height={700}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h3 className="font-display text-2xl font-semibold text-white">
              Sasho Cvetanoski — Founder &amp; Lead Engineer
            </h3>
            <p className="mt-1 text-sm text-accent-400">
              Electrical engineer · Software builder · Skopje, North Macedonia
            </p>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-white/75">
              <p>
                I'm a graduate electrical engineer who turned a fascination with systems
                into a career building software. The mindset stayed the same — understand
                how a system actually behaves, find the real constraint, and design
                something that solves it cleanly.
              </p>
              <p>
                I started Nexsol Labs to do exactly that for companies: build the
                custom software a business actually needs, instead of bending its
                workflow around whatever off-the-shelf tool was cheapest to subscribe
                to. Internal operations platforms, SaaS products, AI-powered features —
                shaped to the people who'll use them every day.
              </p>
              <p>
                If you're weighing whether to build something custom, talk to me
                directly. No sales funnel — you'll get an honest read on whether it's
                worth doing, and what it would actually take.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">
                Get in touch
              </Link>
              <Link to="/projects" className="btn-ghost">
                See our work
              </Link>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* TEAM */}
      <Section
        eyebrow="The team"
        title="Small by design — four engineers, no middle layer."
      >
        <div className="max-w-3xl space-y-4 text-base leading-relaxed text-white/75">
          <p>
            We are four engineers right now — the people you talk to are the same
            people writing your code. No account managers, no offshore hand-offs,
            no junior developer learning on your project.
          </p>
          <p>
            When a project needs a specialist we do not have in-house, we bring in
            trusted collaborators we have shipped with before. The team that
            starts your build is the team that finishes it.
          </p>
        </div>
      </Section>

      {/* VALUES */}
      <Section
        eyebrow="What we believe"
        title="A handful of principles, applied consistently."
      >
        <div className="grid gap-5 md:grid-cols-2">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="card"
            >
              <h3 className="font-display text-lg font-semibold text-white">
                {v.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

    </>
  );
}
